import { Color, Spacing } from '@persona-id/design-tokens';
import { uniqueId } from 'lodash';
import { forwardRef, ReactNode, useCallback, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';

import useFormField from 'design-system/components/FormControl/useFormField';
import { Label } from 'design-system/components/Text';
import hoverableMixin from 'design-system/mixins/hoverable';
import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';

interface SwitchSizes {
  width: number;
  height: number;
}

const SWITCH_SIZES: SwitchSizes = {
  width: 32,
  height: 18,
};

interface SliderSizes {
  diameter: number;
  offset: number;
}

const SLIDER_SIZES: SliderSizes = {
  diameter: 14,
  offset: 2,
};

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  position: relative;
`;

const Switch = styled.div<
  Pick<ToggleSliderProps, 'disabled'> & {
    checked: boolean;
    focused: boolean;
  }
>`
  ${hoverableMixin};

  position: relative;
  display: inline-flex;
  height: ${SWITCH_SIZES.height}px;
  width: ${SWITCH_SIZES.width}px;
  border-radius: 1000px;

  &:before {
    position: absolute;
    content: '';
    height: ${SWITCH_SIZES.height}px;
    width: ${SWITCH_SIZES.width}px;
    top: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ theme, checked, disabled }) =>
      disabled
        ? checked
          ? Color.CoreBlue300
          : theme.color.background.disabled
        : checked
          ? Color.BackgroundInputCheckboxChecked
          : Color.BackgroundInputToggle};
    transition: ${TransitionDuration.Medium};
    border-radius: 1000px;
  }

  &:after {
    position: absolute;
    content: '';
    height: ${SLIDER_SIZES.diameter}px;
    width: ${SLIDER_SIZES.diameter}px;
    bottom: ${SLIDER_SIZES.offset}px;
    background-color: ${({ theme }) => theme.color.background.primary};
    transition: ${TransitionDuration.Medium};
    border-radius: 1000px;

    right: ${SLIDER_SIZES.offset}px;

    ${({ checked }) =>
      !checked &&
      css`
        transform: translateX(-${getSlideDistance()}px);
      `}
  }
`;

const ToggleLabel = styled(Label)<Pick<ToggleSliderProps, 'disabled'>>`
  cursor: ${({ disabled }) => (disabled ? 'initial' : 'pointer')};
  color: ${Color.TextSecondary};

  padding: 0 ${Spacing[2]};
  white-space: nowrap;

  ${({ theme, disabled }) =>
    disabled &&
    css`
      color: ${theme.color.text.disabled};
    `}
`;

function getSlideDistance() {
  return SWITCH_SIZES.width - SLIDER_SIZES.diameter - SLIDER_SIZES.offset * 2;
}

const HiddenInput = styled.input`
  height: 0;
  width: 0;
  margin: -1px;
  border: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  position: absolute;
`;

export interface ToggleSliderProps {
  className?: string;
  disabled?: boolean;
  titleOff?: string;
  titleOn?: string;
  onChange?: (value: boolean) => void;
  value: boolean | null | undefined | '';
  id?: string;
  labelOn?: ReactNode;
  labelOff?: ReactNode;
  'data-test'?: string;
}

const ToggleSlider = forwardRef<HTMLDivElement, ToggleSliderProps>(function ToggleSlider(
  {
    className,
    disabled,
    titleOff = 'Off',
    titleOn = 'On',
    onChange = () => undefined,
    value,
    id,
    labelOn,
    labelOff,
    'data-test': dataTest,
  },
  ref,
) {
  const field = useFormField({ disabled });
  const generatedId = useMemo(() => uniqueId('pds_toggleslider__'), []);
  const label = value ? labelOn : labelOff || labelOn;

  const [focused, { enable: setFocus, disable: clearFocus }] = useToggle();

  const handleChange = useCallback(() => {
    onChange(!value);
    inputRef.current?.focus();
  }, [onChange, value]);

  const inputRef = useRef<HTMLInputElement>();

  return (
    <Container
      className={className}
      data-test={dataTest}
      title={value ? titleOn : titleOff}
      aria-readonly={field.disabled}
      ref={ref}
    >
      {label != null && (
        <ToggleLabel htmlFor={generatedId} disabled={field.disabled}>
          {label}
        </ToggleLabel>
      )}
      <Switch
        id={id}
        checked={!!value}
        disabled={field.disabled}
        focused={focused}
        onClick={field.disabled ? undefined : handleChange}
      />
      <HiddenInput
        id={generatedId}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ref={inputRef}
        data-test-toggle-status={value ? 'on' : 'off'}
        disabled={field.disabled}
        type='checkbox'
        checked={!!value}
        readOnly={true}
        onChange={disabled ? undefined : handleChange}
        onFocus={setFocus}
        onBlur={clearFocus}
        aria-checked={!!value}
        role='switch'
        {...(label
          ? { 'aria-labelledby': generatedId }
          : { 'aria-label': value ? 'Toggle on' : 'Toggle off' })}
      />
    </Container>
  );
});

export default ToggleSlider;
